<template>
    <div>
        <DataTable
            :perPage="itemsPerPage"
            :columns="tableColumns"
            :items="paginatedItems"
            :currentPage="currentPage"
            :totalPages="totalPages"
            :selectedItems="selectedItems"
            :enableDragDrop="true"
            :enableMultiSelect="true"
            @page-changed="handlePageChange"
            @item-selected="toggleItemSelection"
            @select-all-toggled="toggleSelectAll"
            @drag-end="handleDragEnd"
            @sort="handleSort"
            @per-page-changed="handlePerPageChange"
            :sortBy="sortBy"
            :sortOrder="sortOrder"
        >
            <!-- Toolbar -->
            <template #toolbar>
                <!-- Breadcrumb Navigation -->
                <div v-if="breadcrumbs.length > 0" class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-mb-4">
                    <span
                        class="ppv2-text-[#7D2DBD] ppv2-cursor-pointer poppinsSemiBold ppv2-text-2xl hover:ppv2-underline"
                        @click="navigateToRoot"
                    >
                        {{ $t('food.navigation.categories') }}
                    </span>
                    <span v-for="(crumb, index) in breadcrumbs" :key="crumb.id" class="ppv2-flex ppv2-items-center ppv2-gap-2">
                        <ChevronRight :size="20" class="ppv2-text-gray-400" />
                        <span
                            class="poppinsSemiBold ppv2-text-2xl"
                            :class="
                                index === breadcrumbs.length - 1
                                    ? 'ppv2-text-black'
                                    : 'ppv2-text-[#7D2DBD] ppv2-cursor-pointer hover:ppv2-underline'
                            "
                            @click="index < breadcrumbs.length - 1 ? navigateToBreadcrumb(index) : null"
                        >
                            {{ crumb.name }}
                        </span>
                    </span>
                </div>

                <div class="ppv2-flex ppv2-justify-between ppv2-items-center ppv2-mb-6">
                    <div class="ppv2-flex ppv2-flex-wrap ppv2-gap-3">
                        <!-- Search Input -->
                        <div class="ppv2-relative">
                            <Search v-model="searchQuery" :emitGlobal="false" @search-updated="handleSearchUpdate" />
                        </div>

                        <!-- Export Button -->
                        <ExportSelect
                            :options="[
                                { label: 'CSV', value: 'csv' },
                                { label: 'Excel', value: 'excel' },
                            ]"
                            :disabled="isExporting"
                            @export-selected="exportCategories"
                        />

                        <!-- Filter Button -->
                        <button
                            @click="openFilterModal"
                            class="filterButton ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-border-black ppv2-border-opacity-20 ppv2-text-black hover:ppv2-bg-opacity-80 ppv2-transition-all"
                        >
                            <LayoutGrid :size="17" />
                            <span class="poppinsLight" :style="{ fontSize: normalizeFontSize(15) }">
                                {{ $t('products.actions.filter') }}
                            </span>
                            <span
                                v-if="activeFiltersCount > 0"
                                class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-bg-[#7D2DBD] ppv2-text-white ppv2-text-[10px] ppv2-w-5 ppv2-h-5 ppv2-rounded-full poppinsSemiBold"
                            >
                                {{ activeFiltersCount }}
                            </span>
                        </button>

                        <AdvancedFilterModal
                            v-model="showFilterModal"
                            :initialFilters="advancedFilters"
                            :availableFilters="['tags', 'status']"
                            :tags="filterData.tags"
                            @apply="handleAdvancedFilters"
                        />
                    </div>

                    <BaseButton
                        variant="purple"
                        :custom-height="40"
                        :custom-font-size="14"
                        custom-class="ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px]"
                        @click="openCreateTab"
                    >
                        <Plus :size="17" />
                        <span class="poppinsRegular">{{ $t('food.carte.add') }}</span>
                    </BaseButton>
                </div>
            </template>

            <!-- Column: Name (colored badge - clickable) -->
            <template #column-name="{ item }">
                <span
                    class="ppv2-inline-block ppv2-px-3 ppv2-py-2 ppv2-rounded-lg ppv2-cursor-pointer hover:ppv2-opacity-80 ppv2-transition-opacity"
                    :style="{
                        backgroundColor: item.color,
                        color: 'black',
                    }"
                    @click="navigateToCategory(item)"
                >
                    {{ item.name }}
                </span>
            </template>

            <!-- Column: Subcategories Count -->
            <template #column-subcategories_count="{ item }">
                <a
                    href="#"
                    class="ppv2-underline ppv2-text-black"
                    @click.prevent="item.subcategories_count > 0 ? navigateToCategory(item) : null"
                    :class="{ 'ppv2-cursor-default': item.subcategories_count === 0 }"
                >
                    {{ item.subcategories_count }}
                </a>
            </template>

            <!-- Column: Products Count -->
            <template #column-products_count="{ item }">
                <a
                    href="#"
                    class="ppv2-underline ppv2-text-black"
                    @click.prevent="item.products_count > 0 ? viewProductsForCategory(item) : null"
                    :class="{ 'ppv2-cursor-default': item.products_count === 0 }"
                >
                    {{ item.products_count }}
                </a>
            </template>

            <!-- Column: Tags -->
            <template #column-tags="{ item }">
                <div class="ppv2-flex ppv2-flex-wrap ppv2-gap-1">
                    <a v-for="tag in item.tags || []" :key="tag" class="ppv2-underline ppv2-text-black"> #{{ tag }} </a>
                </div>
            </template>

            <!-- Column: Status -->
            <template #column-status="{ item }">
                <button
                    @click="toggleStatus(item)"
                    class="ppv2-min-w-[125px] ppv2-inline-flex ppv2-items-center ppv2-gap-2 ppv2-px-3 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-transition-all"
                    :class="
                        item.status === 'actif'
                            ? 'ppv2-border-[#206A64] ppv2-bg-[#06B217] ppv2-bg-opacity-20'
                            : 'ppv2-border-[#4E4E4E] ppv2-bg-[#474747] ppv2-bg-opacity-20'
                    "
                >
                    <div
                        class="ppv2-w-9 ppv2-h-5 ppv2-rounded-full ppv2-flex ppv2-items-center ppv2-transition-all"
                        :class="
                            item.status === 'actif'
                                ? 'ppv2-bg-[#206A64] ppv2-justify-end'
                                : 'ppv2-bg-[#4E4E4E] ppv2-justify-start'
                        "
                    >
                        <div class="ppv2-w-3.5 ppv2-h-3.5 ppv2-bg-white ppv2-rounded-full ppv2-mx-0.5"></div>
                    </div>
                    <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                        item.status === 'actif' ? $t('food.status.active') : $t('food.status.inactive')
                    }}</span>
                </button>
            </template>

            <!-- Column: Actions -->
            <template #column-actions="{ item }">
                <div class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-3">
                    <button
                        @click="openEditTab(item.id)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('common.edit')"
                    >
                        <SquarePen :size="18" />
                    </button>
                    <button
                        @click="duplicateItem(item.id)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('common.duplicate')"
                    >
                        <CopyPlus :size="18" />
                    </button>
                    <button
                        class="ppv2-p-1 ppv2-text-[#FF9C9C] hover:ppv2-bg-[#FF9C9C] hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('common.delete')"
                        @click="deleteSingleItem(item.id)"
                    >
                        <Trash2 :size="18" />
                    </button>
                </div>
            </template>

            <!-- Bulk Actions Slot -->
            <template #bulk-actions="{ selectedCount }">
                <span class="poppinsRegular ppv2-text-black" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ $t('food.messages.selected_items', { count: selectedCount }) }}
                </span>

                <div class="ppv2-flex ppv2-gap-2">
                    <BaseButton
                        variant="outline"
                        @click="openBulkTagModal"
                        customClass="!ppv2-p-0 !ppv2-h-[30px] !ppv2-px-3 !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                    >
                        <Tag :size="18" />
                        <span :style="{ fontSize: normalizeFontSize(14) }">{{ $t('food.categories.bulk.edit_tag') }}</span>
                    </BaseButton>

                    <BaseButton
                        variant="dangerOutline"
                        @click="deleteSelectedItems"
                        customClass="!ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                    >
                        <Trash2 :size="18" />
                        <span :style="{ fontSize: normalizeFontSize(14) }">{{ $t('common.delete') }}</span>
                    </BaseButton>
                </div>
            </template>
        </DataTable>

        <!-- Create/Edit Modal -->
        <!-- Create/Edit Modal -->
        <CategoryModal
            v-model="isCreateModalOpen"
            :isEditMode="isEditMode"
            :isSaving="isSaving"
            :categoryForm="categoryForm"
            :parentCategories="parentCategories"
            :availableColors="availableColors"
            :tagsOptions="tagsOptions"
            :errors="formErrors"
            @save="saveCategory"
        />
        <!-- Delete Confirmation Popup -->
        <CustomDialog v-model="showDeletePopup" :defaultTitle="$t('common.delete')" noScroll>
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular">{{ deletePopupDescription }}</p>
            </template>
            <template #actions>
                <BaseButton
                    variant="outline"
                    @click="showDeletePopup = false"
                    :customClass="`!ppv2-h-[45px] !ppv2-min-w-[170px]`"
                    >{{ $t('common.cancel') }}</BaseButton
                >
                <BaseButton variant="danger" @click="confirmDelete" :customClass="`!ppv2-h-[45px] !ppv2-min-w-[170px]`">{{
                    $t('common.delete')
                }}</BaseButton>
            </template>
        </CustomDialog>

        <!-- Duplicate Confirmation Popup -->
        <CustomDialog v-model="showDuplicatePopup" :defaultTitle="$t('common.duplicate')" height="250px" minHeight="250px">
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular">{{ duplicatePopupDescription }}</p>
            </template>
            <template #actions>
                <BaseButton
                    variant="outline"
                    @click="showDuplicatePopup = false"
                    :customClass="`!ppv2-h-[45px] !ppv2-min-w-[170px]`"
                    >{{ $t('common.cancel') }}</BaseButton
                >
                <BaseButton variant="primary" @click="confirmDuplicate" :customClass="`!ppv2-h-[45px] !ppv2-min-w-[170px]`">{{
                    $t('common.confirm')
                }}</BaseButton>
            </template>
        </CustomDialog>

        <!-- Bulk Tag Modal -->
        <CustomDialog
            v-model="showBulkTagModal"
            :defaultTitle="$t('food.categories.bulk.edit_tag')"
            heightClass="ppv2-max-h-[60vh]"
        >
            <template #content>
                <p class="ppv2-text-sm ppv2-text-gray-600 ppv2-mb-4">
                    {{ $t('food.messages.confirm_bulk_tags', { count: selectedItems.length }) }}
                </p>
                <div class="ppv2-flex ppv2-flex-col" :style="{ gap: normalizeSize(16, true) }">
                    <!-- Mode Selection -->
                    <div class="ppv2-flex ppv2-mt-4" :style="{ gap: normalizeSize(16, true) }">
                        <label class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-cursor-pointer">
                            <input type="radio" v-model="bulkTagForm.mode" value="add" class="ppv2-accent-[#7D2DBD]" />
                            <span class="poppinsRegular">{{ $t('common.add') }}</span>
                        </label>
                        <label class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-cursor-pointer">
                            <input type="radio" v-model="bulkTagForm.mode" value="remove" class="ppv2-accent-[#7D2DBD]" />
                            <span class="poppinsRegular">{{ $t('common.remove') }}</span>
                        </label>
                        <label class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-cursor-pointer">
                            <input type="radio" v-model="bulkTagForm.mode" value="replace" class="ppv2-accent-[#7D2DBD]" />
                            <span class="poppinsRegular">{{ $t('common.replace') }}</span>
                        </label>
                    </div>

                    <!-- Tag Selection -->
                    <div class="ppv2-flex ppv2-flex-col ppv2-gap-2">
                        <BaseSelect
                            v-model="bulkTagForm.tags"
                            :options="availableTags"
                            optionLabel="label"
                            :reduce="(opt) => opt.label"
                            multiple
                            taggable
                            :placeholder="$t('food.productForm.tagsPlaceholder')"
                            class="ppv2-w-full"
                            fixed-dropdown
                        />
                    </div>
                </div>
            </template>
            <template #actions>
                <BaseButton
                    variant="outline"
                    @click="showBulkTagModal = false"
                    :customClass="`!ppv2-h-[45px] !ppv2-min-w-[170px]`"
                    >{{ $t('common.cancel') }}</BaseButton
                >
                <BaseButton variant="primary" @click="submitBulkTags" :customClass="`!ppv2-h-[45px] !ppv2-min-w-[170px]`">{{
                    $t('common.apply')
                }}</BaseButton>
            </template>
        </CustomDialog>

        <!-- Bulk Discount Modal -->
        <CustomDialog
            v-model="showBulkDiscountModal"
            :defaultTitle="$t('food.carte.bulk.apply_discount')"
            heightClass="ppv2-max-h-[50vh]"
        >
            <template #content>
                <div class="ppv2-flex ppv2-flex-col" :style="{ gap: normalizeSize(16, true) }">
                    <p class="poppinsRegular ppv2-text-sm ppv2-text-gray-500">
                        {{ $t('food.messages.confirm_bulk_discount', { count: selectedItems.length }) }}
                    </p>

                    <!-- Discount Type -->
                    <div class="ppv2-flex" :style="{ gap: normalizeSize(16, true) }">
                        <label class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-cursor-pointer">
                            <input
                                type="radio"
                                v-model="bulkDiscountForm.type"
                                value="percentage"
                                class="ppv2-accent-[#7D2DBD]"
                            />
                            <span class="poppinsRegular">{{ $t('food.carte.bulk.percentage') }}</span>
                        </label>
                        <label class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-cursor-pointer">
                            <input type="radio" v-model="bulkDiscountForm.type" value="amount" class="ppv2-accent-[#7D2DBD]" />
                            <span class="poppinsRegular">{{
                                $t('food.carte.bulk.fixed_amount', { symbol: $symbolCurrency })
                            }}</span>
                        </label>
                    </div>

                    <!-- Value Input -->
                    <div class="ppv2-flex ppv2-flex-col ppv2-gap-2">
                        <label class="poppinsMedium ppv2-text-sm">{{ $t('food.carte.bulk.value') }}</label>
                        <input
                            type="number"
                            step="any"
                            v-model.number="bulkDiscountForm.value"
                            class="ppv2-w-full ppv2-px-4 ppv2-py-3 ppv2-rounded-xl ppv2-border ppv2-border-gray-300 focus:ppv2-border-[#7D2DBD] ppv2-outline-none poppinsRegular"
                            min="0"
                            :max="bulkDiscountForm.type === 'percentage' ? 100 : undefined"
                        />
                    </div>
                </div>
            </template>
            <template #actions>
                <BaseButton
                    variant="outline"
                    @click="showBulkDiscountModal = false"
                    :customClass="`!ppv2-h-[45px] !ppv2-min-w-[170px]`"
                    >{{ $t('common.cancel') }}</BaseButton
                >
                <BaseButton variant="primary" @click="submitBulkDiscount" :customClass="`!ppv2-h-[45px] !ppv2-min-w-[170px]`">{{
                    $t('common.apply')
                }}</BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import { LayoutGrid, ChevronDown, ChevronRight, Plus, SquarePen, CopyPlus, Trash2, Tag, Percent, Loader2 } from 'lucide-vue'
import normalizeSize from '../../../utils/normalizeSize'
import Search from '../Experience/components/Search.vue'
import ExportSelect from '../../Shared/ExportSelect.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import DataTable from '../../Shared/DataTable.vue'
import CategoryModal from './CategoryModal.vue'
import BaseSelect from '../../Shared/BaseSelect.vue'
import AdvancedFilterModal from '../../Shared/AdvancedFilterModal.vue'
import axios from 'axios'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../../utils/perPage'

export default {
    name: 'BoFoodV3Categories',
    components: {
        LayoutGrid,
        ChevronDown,
        ChevronRight,
        Plus,
        SquarePen,
        CopyPlus,
        Trash2,
        Tag,
        Percent,
        Loader2,
        Search,
        ExportSelect,
        BaseButton,
        CustomDialog,
        DataTable,
        CategoryModal,
        BaseSelect,
        AdvancedFilterModal,
    },
    props: {
        establishment: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            searchQuery: '',
            currentPage: 1,
            itemsPerPage: getPerPage('food_categories_items_per_page', 10),
            sortBy: 'order',
            sortOrder: 'asc',
            selectedItems: [],
            showDeletePopup: false,
            deletePopupDescription: '',
            deleteAction: null,
            itemToDelete: null,
            items: [],
            totalPages: 1,

            // Navigation State
            breadcrumbs: [], // Array of { id, name, color }
            currentParentId: null, // null = root level

            // Modal State
            isCreateModalOpen: false,
            isEditMode: false,
            isSaving: false,
            isExporting: false,
            isSavingPositions: false,

            // Duplicate Popup
            showDuplicatePopup: false,
            duplicatePopupDescription: '',
            itemToDuplicate: null,

            showFilterModal: false,
            advancedFilters: {
                status: 'all',
                tag_ids: [],
            },
            filterData: {
                tags: [],
            },

            // Bulk Action Modals
            showBulkTagModal: false,
            showBulkDiscountModal: false,
            bulkTagForm: {
                mode: 'add', // add, remove, replace
                tags: [],
            },
            bulkDiscountForm: {
                type: 'percentage', // percentage, amount
                value: 0,
            },

            // Form Data
            categoryForm: {
                id: null,
                name: '',
                parent_id: null,
                slug: '',
                tags: [],
                color: '#FFD7D7', // Default color
                active: true, // Default to active
                image: null,
            },
            formErrors: {},

            // Data for selects
            parentCategories: [],
            tagsOptions: [],
            availableColors: [
                '#FFD7D7',
                '#FFF4D7',
                '#D7F2FF',
                '#D7FFDC',
                '#FFEBD7',
                '#FAD7FF',
                '#8B9EFF',
                '#FFAB91',
                '#FFF59D',
                '#CE93D8',
                '#B39DDB',
            ],
        }
    },
    computed: {
        tableColumns() {
            return [
                { key: 'name', label: this.$t('food.columns.name'), sortable: true, type: 'text' },
                {
                    key: 'subcategories_count',
                    label: this.$t('food.categories.columns.subcategories_count'),
                    sortable: true,
                    type: 'number',
                },
                {
                    key: 'products_count',
                    label: this.$t('food.categories.columns.content'),
                    sortable: true,
                    type: 'number',
                },
                { key: 'tags', label: 'Tags', sortable: false, type: 'text' },
                { key: 'status', label: this.$t('food.columns.status'), sortable: true, type: 'badge' },
                { key: 'actions', label: this.$t('food.columns.actions'), sortable: false, type: 'actions' },
            ]
        },
        paginatedItems() {
            return this.items
        },
        activeFiltersCount() {
            let count = 0
            if (this.advancedFilters.status !== 'all') count++
            if (this.advancedFilters.tag_ids?.length > 0) count++
            return count
        },
        availableTags() {
            // Return all tags - vue-select handles hiding selected ones in dropdown
            return this.tagsOptions
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        handlePageChange(page) {
            this.currentPage = page
            this.fetchData()
        },
        handlePerPageChange(val) {
            this.itemsPerPage = Number(val)
            setPerPage('food_categories_items_per_page', this.itemsPerPage)
            this.currentPage = 1
            this.fetchData()
        },
        handleSearchUpdate(val) {
            this.searchQuery = val
            this.currentPage = 1
            this.fetchData()
        },
        toggleItemSelection(id) {
            const idx = this.selectedItems.indexOf(id)
            idx > -1 ? this.selectedItems.splice(idx, 1) : this.selectedItems.push(id)
        },
        toggleSelectAll() {
            this.selectedItems = this.selectedItems.length === this.items.length ? [] : this.items.map((i) => i.id)
        },
        async toggleStatus(item) {
            const prev = item.status
            item.status = prev === 'actif' ? 'inactif' : 'actif'
            try {
                await axios.patch('/back-office/v3/food/update-status', {
                    id: item.id,
                    active: item.status === 'actif',
                    type: 'category',
                })
            } catch {
                item.status = prev
            }
        },
        handleSort(key) {
            if (this.sortBy === key) {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortBy = key
                this.sortOrder = 'asc'
            }
            this.fetchData()
        },
        async openFilterModal() {
            if (this.filterData.tags.length === 0) {
                try {
                    const response = await axios.get('/back-office/v3/food/get-filters-data')
                    this.filterData = response.data.data
                } catch (error) {
                    this.$toast.error(error.response.data.message)
                }
            }
            this.showFilterModal = true
        },
        handleAdvancedFilters(filters) {
            this.advancedFilters = filters
            this.currentPage = 1
            this.fetchData()
        },
        deleteSingleItem(id) {
            this.deleteAction = 'single'
            this.itemToDelete = id
            this.deletePopupDescription = this.$t('food.messages.confirm_delete_category')
            this.showDeletePopup = true
        },
        deleteSelectedItems() {
            if (!this.selectedItems.length) return
            this.deleteAction = 'multiple'
            this.deletePopupDescription = this.$t('food.messages.confirm_delete_categories', { count: this.selectedItems.length })
            this.showDeletePopup = true
        },
        async confirmDelete() {
            const ids = this.deleteAction === 'single' ? [this.itemToDelete] : this.selectedItems

            // Use unified bulkAction endpoint for delete as well
            try {
                await axios.post('/back-office/v3/food/categories/bulk-action', {
                    ids: ids,
                    action: 'delete',
                })
                this.fetchData()
                this.selectedItems = []
                this.showDeletePopup = false
                this.$emit('refresh-stats')
            } catch (e) {
                alert(this.$t('food.messages.delete_error'))
            }
        },

        duplicateItem(itemId) {
            this.itemToDuplicate = itemId
            this.duplicatePopupDescription = this.$t('food.messages.confirm_duplicate_category')
            this.showDuplicatePopup = true
        },
        async confirmDuplicate() {
            try {
                const response = await axios.post('/back-office/v3/food/categories/duplicate', { id: this.itemToDuplicate })
                if (response.data.success) {
                    this.$toast?.success(this.$t('food.categories.updated_success'))
                    this.fetchData()
                    this.$emit('refresh-stats')
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error duplicating category:', error)
                alert(this.$t('food.messages.duplicate_error'))
            } finally {
                this.showDuplicatePopup = false
                this.itemToDuplicate = null
            }
        },

        // Bulk Action Methods
        openBulkTagModal() {
            this.bulkTagForm = { mode: 'add', tags: [] }
            this.fetchTags()
            this.showBulkTagModal = true
        },
        openBulkDiscountModal() {
            this.bulkDiscountForm = { type: 'percentage', value: 0 }
            this.showBulkDiscountModal = true
        },
        async submitBulkTags() {
            try {
                await axios.post('/back-office/v3/food/categories/bulk-action', {
                    ids: this.selectedItems,
                    action: 'update_tags',
                    data: this.bulkTagForm,
                })
                this.showBulkTagModal = false
                this.fetchData()
                this.selectedItems = []
            } catch (e) {
                console.error(e)
                alert(this.$t('food.messages.save_error'))
            }
        },
        async submitBulkDiscount() {
            try {
                await axios.post('/back-office/v3/food/categories/bulk-action', {
                    ids: this.selectedItems,
                    action: 'apply_discount',
                    data: this.bulkDiscountForm,
                })
                this.showBulkDiscountModal = false
                this.fetchData()
                this.selectedItems = []
            } catch (e) {
                console.error(e)
                alert(this.$t('food.messages.save_error'))
            }
        },

        // Modal Methods
        async fetchParentCategories() {
            try {
                const res = await fetch('/back-office/v3/food/categories/fast-list')
                const json = await res.json()
                // Filter out current category if in edit mode to avoid circular dependency
                this.parentCategories = (json.data || []).filter((c) => c.id !== this.categoryForm.id)
            } catch (e) {
                console.error(e)
            }
        },

        async fetchTags() {
            try {
                const res = await fetch('/back-office/v3/food/categories/tags-list')
                const json = await res.json()
                this.tagsOptions = (json.data || []).map((tag) => ({ label: tag }))
            } catch (e) {
                console.error(e)
            }
        },

        openCreateTab() {
            this.isEditMode = false
            this.categoryForm = {
                id: null,
                name: '',
                parent_id: null,
                slug: '',
                tags: [],
                color: '#ead3ff',
                active: true,
                image: null,
            }
            this.fetchParentCategories()
            this.fetchTags()
            this.formErrors = {}
            this.isCreateModalOpen = true
        },

        openEditTab(id) {
            const item = this.items.find((i) => i.id === id)
            if (!item) return

            this.isEditMode = true
            this.categoryForm = {
                id: item.id,
                name: item.name,
                parent_id: item.parent_id,
                slug: item.slug || '', // Assuming slug exists on item
                tags: item.tags || [],
                color: item.color || '#ead3ff',
                active: item.status === 'actif',
                image: item.image || null,
            }
            this.fetchParentCategories()
            this.fetchTags()
            this.formErrors = {}
            this.isCreateModalOpen = true
        },

        async saveCategory() {
            if (!this.categoryForm.name) {
                alert(this.$t('food.messages.name_required'))
                return
            }
            this.formErrors = {}
            this.isSaving = true
            try {
                const url = this.isEditMode ? '/back-office/v3/food/categories/update' : '/back-office/v3/food/categories/store'
                const formData = new FormData()
                formData.append('name', this.categoryForm.name)
                if (this.categoryForm.parent_id) formData.append('parent_id', this.categoryForm.parent_id)
                if (this.categoryForm.slug) formData.append('slug', this.categoryForm.slug)
                formData.append(
                    'color',
                    this.categoryForm.color.startsWith('#') ? this.categoryForm.color : '#' + this.categoryForm.color
                )
                formData.append('active', this.categoryForm.active ? 1 : 0)
                if (this.categoryForm.tags && this.categoryForm.tags.length) {
                    this.categoryForm.tags.forEach((tag) => formData.append('tags[]', tag))
                } else if (this.isEditMode) {
                    formData.append('remove_tags', '1')
                }

                if (this.categoryForm.image instanceof File) {
                    formData.append('image', this.categoryForm.image)
                } else if (this.categoryForm.image === null) {
                    formData.append('remove_image', '1')
                }

                if (this.isEditMode) formData.append('id', this.categoryForm.id)

                await axios.post(url, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })

                this.isCreateModalOpen = false
                this.fetchData()
                this.$emit('refresh-stats')
            } catch (e) {
                console.error(e)
                if (e.response && e.response.status === 422) {
                    this.formErrors = e.response.data.errors
                } else {
                    alert(this.$t('food.messages.save_error'))
                }
            } finally {
                this.isSaving = false
            }
        },

        async fetchData() {
            try {
                const params = {
                    search: this.searchQuery,
                    currentPage: this.currentPage,
                    itemsPerPage: this.itemsPerPage,
                    sortBy: this.sortBy,
                    sortOrder: this.sortOrder,
                    parent_id: this.currentParentId,
                    ...this.advancedFilters,
                }

                const response = await axios.get('/back-office/v3/food/categories/list', { params })
                this.items = response.data.data || []
                this.totalPages = response.data.last_page || 1
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('API error:', error)
            }
        },

        // Navigation Methods
        navigateToCategory(item) {
            // Add to breadcrumbs
            this.breadcrumbs.push({
                id: item.id,
                name: item.name,
                color: item.color,
            })
            this.currentParentId = item.id
            this.currentPage = 1
            this.selectedItems = []
            this.fetchData()
            this.$emit('breadcrumb-change', true)
        },
        navigateToRoot() {
            this.breadcrumbs = []
            this.currentParentId = null
            this.currentPage = 1
            this.selectedItems = []
            this.fetchData()
            this.$emit('breadcrumb-change', false)
        },
        navigateToBreadcrumb(index) {
            // Navigate to a specific breadcrumb
            const crumb = this.breadcrumbs[index]
            this.breadcrumbs = this.breadcrumbs.slice(0, index + 1)
            this.currentParentId = crumb.id
            this.currentPage = 1
            this.selectedItems = []
            this.fetchData()
            this.$emit('breadcrumb-change', this.breadcrumbs.length > 0)
        },
        viewProductsForCategory(item) {
            // Emit event to parent to switch to products tab filtered by this category
            this.$emit('view-products', {
                categoryId: item.id,
                categorySlug: item.slug,
                categoryName: item.name,
            })
        },
        async exportCategories(format = 'csv') {
            if (this.isExporting) return

            this.isExporting = true
            try {
                const res = await axios.get('/back-office/v3/food/categories/export', {
                    params: { format },
                    responseType: 'blob',
                })

                // Create a link to download the file
                const url = window.URL.createObjectURL(new Blob([res.data]))
                const link = document.createElement('a')
                link.href = url

                // Extract filename from content-disposition if possible
                const contentDisposition = res.headers['content-disposition'] || res.headers['Content-Disposition']
                let fileName = `categories_export_${format}.${format === 'pdf' ? 'pdf' : format === 'excel' ? 'xlsx' : 'csv'}`
                if (contentDisposition) {
                    const fileNameMatch = contentDisposition.match(/filename=(?:"([^"]+)"|([^;]+))/)
                    if (fileNameMatch) {
                        fileName = fileNameMatch[1] || fileNameMatch[2]
                    }
                }

                link.setAttribute('download', fileName)
                document.body.appendChild(link)
                link.click()

                // Cleanup
                document.body.removeChild(link)
                window.URL.revokeObjectURL(url)
            } catch (e) {
                console.error(e)
                alert(this.$t('food.messages.export_error'))
            } finally {
                this.isExporting = false
            }
        },
        async handleDragEnd(event) {
            const { oldIndex, newIndex } = event

            if (oldIndex === newIndex) return

            // Move the item locally
            const movedItem = this.items.splice(oldIndex, 1)[0]
            this.items.splice(newIndex, 0, movedItem)

            // Calculate start order index based on pagination
            const startOrder = (this.currentPage - 1) * this.itemsPerPage
            const changedPositions = []

            // Re-assign order based on new index
            this.items.forEach((item, index) => {
                const newOrder = startOrder + index

                // If order changed, add to update list
                changedPositions.push({
                    id: item.id,
                    order: newOrder,
                })
                item.order = newOrder
            })

            // Save if there are changes
            if (changedPositions.length > 0) {
                await this.savePositions(changedPositions)
            }
        },
        async savePositions(positions) {
            if (this.isSavingPositions) return
            this.isSavingPositions = true

            try {
                await axios.put('/back-office/v3/food/categories/update-positions', {
                    positions,
                })
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error saving positions:', error)
                this.fetchData()
            } finally {
                this.isSavingPositions = false
            }
        },
    },
    mounted() {
        this.fetchData()
        // Emit initial breadcrumb state
        this.$emit('breadcrumb-change', this.breadcrumbs.length > 0)
    },
}
</script>

<style scoped lang="scss">
.textLink {
    color: #7d2dbd;
    text-decoration: underline;
}
.dark {
    .textLink {
        color: #a855f7;
    }
}
</style>
